import { useContext, useEffect, useState } from 'react'
import { ModalContext } from '../../../../contexts/modal-context'
import { PopupBodyNoPadding } from '../popup-styles'
import Popup from '../popup'
import { formatToReadableDate, idToNameString } from '../../../../utilities/utils'
import logAnalyticsEvent from '../../../../utilities/firebase-analytics'

import {
  GroupBookingTitle,
  FlexBox,
  ErrorMessage,
  LocationIcon,
  AvailableSeatCounterIcon,
  IconContainer,
  ErrorIcon,
  BookingChipContainer,
  ClubTitle,
  SpacesAvailableText,
  ColumnFlexBox,
  DuplicateErrorMessage,
  ComboBoxIcon
} from './group-booking-popup-styles'
import Pin from './assets/pin.svg'
import SingleAndi from './assets/singleAndi.svg'
import Error from '../assets/error.svg'
import {
  AndiUserProfile,
  BookedSeat,
  BookingErrors,
  BusinessUnit,
  ListOption,
  Team
} from '../../../../types/andTypes'
import GroupBookingComboBox from '../../../atoms/ComboBox/group-booking-combobox/group-booking-combobox'
import GroupBookingChip from '../../../atoms/Chip/group-booking-chip'
import { ApiContext } from '../../../../contexts/api-context'
import { DuplicateTeamMemberError } from '../../../../constants'

type GroupBookingPopupProps = {
  type: string
  header: string
  dateString: string
  selectedLocation: string
  confirmGroupAction: (members: AndiUserProfile[]) => Promise<void>
  ANDis: AndiUserProfile[]
  organiser?: AndiUserProfile
  availableSeats?: number
  selectedANDis?: AndiUserProfile[]
  teams: Team[]
  bookedAndis?: BookedSeat[] | []
  selectedBU?: BusinessUnit | undefined
}
const GroupBookingPopup = ({
  type,
  header,
  dateString,
  selectedLocation,
  confirmGroupAction,
  ANDis,
  organiser,
  availableSeats,
  teams,
  selectedANDis,
  bookedAndis,
  selectedBU
}: GroupBookingPopupProps) => {
  const { setModalOpen } = useContext(ModalContext)
  const { getSeatBookingOnDate, userProfile } = useContext(ApiContext)

  const [isDisabled, setDisabled] = useState(false)

  const [member, setMember] = useState<string>('')
  const [error, setError] = useState('')
  const [bookingError, setBookingError] = useState('')
  const [dataLoaded, setDataLoaded] = useState(false)

  const [selectedMembers, setSelectedMembers] = useState<AndiUserProfile[]>(() => {
    if (selectedANDis) {
      return selectedANDis
    } else {
      if (organiser) {
        return [
          {
            ...organiser,
            name: idToNameString(organiser.userId)
          }
        ] as unknown as AndiUserProfile[]
      }
      return []
    }
  })

  const seatsBookedOnDate = getSeatBookingOnDate(new Date(dateString))
  const [computedAvailableSeats, setComputedAvailableSeats] = useState(1)
  const [computedBookedAndis, setComputedBookedAndis] = useState<BookedSeat[] | undefined>(
    undefined
  )

  let errorMessage = BookingErrors(
    Math.abs(computedAvailableSeats - selectedMembers.length).toString()
  ).NO_MORE_SEATS

  useEffect(() => {
    if (type !== 'amend') {
      if (availableSeats) {
        setComputedAvailableSeats(availableSeats)
        setDataLoaded(true)
      }
    } else {
      setDataLoaded(true)
    }
  }, [availableSeats])

  useEffect(() => {
    setComputedBookedAndis(bookedAndis)
  }, [bookedAndis])

  useEffect(() => {
    if (selectedBU && type === 'amend' && seatsBookedOnDate) {
      const seats = selectedBU?.deskCapacity - seatsBookedOnDate.seats.length
      setComputedBookedAndis(seatsBookedOnDate.seats)
      setComputedAvailableSeats(seats)
      if (selectedMembers.length > seats) {
        errorMessage = BookingErrors(
          Math.abs(seats - selectedMembers.length).toString()
        ).NO_MORE_SEATS
      }
    }
  }, [type])

  const updatedAvailableSeats = computedAvailableSeats - selectedMembers.length

  const [membersAndTeams, setMembersAndTeams] = useState<
    (
      | Team
      | {
          disabled: boolean | undefined
          id: string
          userId: string
          businessUnit: string
          team?: string | undefined
          name?: string | undefined
        }
    )[]
  >([])

  useEffect(() => {
    if (computedBookedAndis !== undefined) {
      setMembersAndTeams(
        [
          ...teams,
          ...ANDis.map(item => {
            return {
              ...item,
              disabled:
                computedBookedAndis && computedBookedAndis.some(andi => andi.userId === item.userId)
            }
          })
        ].sort((itemA, itemB) => itemA.id.localeCompare(itemB.id))
      )
    }
  }, [teams, ANDis, computedBookedAndis])

  const memberSelected = (memberEntered: ListOption) => {
    if (teams.includes(memberEntered as unknown as Team)) {
      const teamMembers = ANDis.filter(
        andi =>
          andi.team === (memberEntered as unknown as Team).name &&
          (andi.businessUnit === selectedBU?.id || andi.businessUnit === userProfile?.businessUnit)
      )
      if (teamMembers.length + selectedMembers.length > computedAvailableSeats) {
        setError(errorMessage)
      }

      const bookedAndiUserIds = bookedAndis?.map(andi => andi.userId)
      const bookedTeamMembers = teamMembers.filter(member =>
        bookedAndiUserIds?.includes(member.userId)
      )
      const nonBookedTeamMembers = teamMembers.filter(
        member => !bookedAndiUserIds?.includes(member.userId)
      )
      nonBookedTeamMembers.forEach(addMember)

      const bookedTeamMemberCount = bookedTeamMembers.length
      if (bookedTeamMemberCount > 0) {
        const bookedMemberClub = bookedTeamMembers[0].businessUnit?.toString().replace(/_/g, ' ')

        if (bookedTeamMemberCount === 1) {
          const firstMemberBooked = bookedTeamMembers[0].name?.toString()
          const errorMessage = DuplicateTeamMemberError(
            undefined,
            firstMemberBooked,
            bookedMemberClub
          )
          setBookingError(errorMessage.ONE_MEMBER_DUPLICATE)
        } else {
          const errorMessage = DuplicateTeamMemberError(
            bookedTeamMemberCount.toString(),
            undefined,
            bookedMemberClub
          )
          setBookingError(errorMessage.MORE_THAN_ONE_MEMBER_BOOKED)
        }
      }
    } else if (member) {
      if (selectedMembers.some(i => i.id === (memberEntered as unknown as AndiUserProfile).id)) {
        removeMember((memberEntered as unknown as AndiUserProfile).id)
        setMember('')
      } else {
        setError('')
        addMember(memberEntered as unknown as AndiUserProfile)
      }
    }
  }

  useEffect(() => {
    setDisabled(selectedMembers.length === 1 || selectedMembers.length > computedAvailableSeats)
    checkSpaces(errorMessage)
  }, [selectedMembers])

  const checkSpaces = (errorMsg: string) => {
    if (
      (type === 'amend' || (type !== 'amend' && dataLoaded)) &&
      selectedMembers.length > computedAvailableSeats
    ) {
      setError(errorMsg)
    } else {
      setError('')
    }
  }

  const removeMember = (id: string) => {
    const newSelectedMembers = selectedMembers.filter(user => user.id !== id)
    setSelectedMembers([...newSelectedMembers])
    setBookingError('')
  }

  const addMember = (andi: AndiUserProfile) => {
    if (!selectedMembers.some(i => i.id === andi.id)) {
      setSelectedMembers(selectedANDis => [...selectedANDis, andi])
      setMember('')
    } else {
      setError(BookingErrors().USER_ALREADY_ADDED)
    }
  }

  const closeModalGroup = () => {
    setModalOpen(false)
    if (header === 'Cancel Booking') {
      logAnalyticsEvent('delete_decision_cancelled')
    } else {
      logAnalyticsEvent('booking_decision_cancelled')
    }
  }

  const errorHandle = (errorPassed: string) => {
    if (errorPassed === '') {
      setError(errorPassed)
    }
    if (errorPassed === 'REMOVE_BOOKING_ERROR') {
      setBookingError('')
    }
    if (!(error === errorMessage || error === BookingErrors().USER_ALREADY_ADDED)) {
      setError(errorPassed)
    }
  }

  const confirmGroupBooking = async () => {
    await confirmGroupAction(selectedMembers)
  }

  const getButtonConfig = (disableButton: boolean) => {
    return [
      {
        text: 'Confirm',
        onClick: confirmGroupBooking,
        disabled: disableButton
      }
    ]
  }

  return (
    <Popup
      header={header}
      hasRedHeader
      buttonConfig={getButtonConfig(isDisabled)}
      onPopupClose={closeModalGroup}
    >
      <PopupBodyNoPadding>
        <ColumnFlexBox>
          <FlexBox>
            <IconContainer>{<LocationIcon src={Pin} />}</IconContainer>
            <ClubTitle>{selectedLocation}</ClubTitle>
          </FlexBox>
          <GroupBookingTitle className="date">
            {formatToReadableDate(dateString)}{' '}
          </GroupBookingTitle>
        </ColumnFlexBox>
        <FlexBox>
          <IconContainer>{<AvailableSeatCounterIcon src={SingleAndi} />}</IconContainer>
          <SpacesAvailableText>
            {updatedAvailableSeats >= 0 ? updatedAvailableSeats : 0} spaces left
          </SpacesAvailableText>
        </FlexBox>
        <ErrorMessage>{error}</ErrorMessage>
        <FlexBox>
          <IconContainer>{<ComboBoxIcon src={SingleAndi} />}</IconContainer>
          <div className="autocomplete">
            {membersAndTeams && membersAndTeams.length > 0 && (
              <GroupBookingComboBox
                listItems={membersAndTeams as unknown as ListOption[]}
                listValue={member}
                disabled={computedAvailableSeats <= selectedMembers.length}
                setListValue={setMember}
                valueProperty="id"
                displayTemplate="{name}"
                returnSelectedItem={memberSelected}
                placeholder={'Add member(s) or team'}
                errorHandle={errorReturned => errorHandle(errorReturned)}
                stickySearch={false}
                selectedMembers={selectedMembers}
                setSelectedMembers={setSelectedMembers}
                organiser={organiser}
                selectedBu={selectedBU}
              />
            )}
          </div>
          {error && <ErrorIcon isLocationNotReady={false} src={Error} />}
        </FlexBox>
        <DuplicateErrorMessage>{bookingError}</DuplicateErrorMessage>
        <BookingChipContainer shouldScroll={selectedMembers.length > 12}>
          {selectedMembers.map((i, index) => (
            <GroupBookingChip
              userName={i.name}
              isOrganiser={index < 1}
              key={i.name}
              onCancel={() => removeMember(i.id)}
              deletable={true}
            />
          ))}
        </BookingChipContainer>
      </PopupBodyNoPadding>
    </Popup>
  )
}

export default GroupBookingPopup
